import { useParams, Link } from 'react-router-dom';
import { articles, getCategoryName } from '@/mocks/articleData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function ArticlePage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const article = articles[slug];

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="pt-20 min-h-screen flex flex-col items-center justify-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 mb-4">
            <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600 mb-4">文章不存在或已下架</p>
          <Link to="/" className="text-accent-600 hover:text-accent-700 font-medium">
            返回首页
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const categoryName = getCategoryName(article.category);
  const relatedArticles = article.relatedArticles
    .map((s) => articles[s])
    .filter(Boolean);

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image: article.cover,
        datePublished: article.date,
        author: {
          '@type': 'Person',
          name: article.author,
          description: article.authorBio,
        },
        publisher: {
          '@type': 'Organization',
          name: '博彩网',
        },
        inLanguage: 'zh-CN',
        url: `https://bo-cai-wang.com/article/${slug}`,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://bo-cai-wang.com' },
          { '@type': 'ListItem', position: 2, name: categoryName, item: `https://bo-cai-wang.com/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title, item: `https://bo-cai-wang.com/article/${slug}` },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 2) }} />
      <Navbar />

      <main className="pt-20">
        {/* Breadcrumb */}
        <div className="px-4 md:px-6 py-3 bg-background-100/50 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-accent-600 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${article.category}`} className="hover:text-accent-600 transition-colors whitespace-nowrap">{categoryName}</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium line-clamp-1">{article.title}</span>
          </div>
        </div>

        {/* Article Header */}
        <section className="py-8 md:py-12 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium hover:bg-accent-200 transition-colors"
              >
                {categoryName}
              </Link>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
              <span className="flex items-center gap-1.5">
                <div className="w-7 h-7 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-user-line text-xs text-accent-600"></i>
                </div>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                阅读 {article.readTime}
              </span>
            </div>

            {/* Cover Image */}
            <div className="rounded-lg overflow-hidden mb-8">
              <img
                src={article.cover}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Article Content */}
            <article
              className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-900 prose-p:text-foreground-700 prose-p:leading-relaxed prose-strong:text-foreground-900 prose-a:text-accent-600 prose-h2:text-xl md:prose-h2:text-2xl prose-h3:text-lg md:prose-h3:text-xl"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Author Card */}
            <div className="mt-10 p-5 bg-background-100/70 border border-background-200/70 rounded-lg flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-user-3-line text-accent-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-900 mb-1">{article.author}</p>
                <p className="text-sm text-foreground-600">{article.authorBio}</p>
              </div>
            </div>

            {/* Tags / Keywords */}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.keywords.split(',').map((kw) => (
                <span key={kw} className="inline-flex items-center px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs">
                  {kw.trim()}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100/70">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">相关文章</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-accent-300/50 transition-all duration-300"
                  >
                    <div className="h-40 overflow-hidden">
                      <img src={ra.cover} alt={ra.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-accent-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-2">{ra.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}